/**
 * Animation types and interfaces for Creatomate-based elements
 * Based on Creatomate API documentation: https://creatomate.com/docs
 */

export interface Animation {
  // Core animation properties
  type: string;
  time?: string | number; // 'start', 'end', or specific time in seconds
  duration?: number; // Duration in seconds

  // Animation behavior
  easing?: EasingType;
  reversed?: boolean; // For exit animations
  transition?: boolean; // For transitioning between elements

  // Animation-specific properties (varies by animation type)
  direction?: AnimationDirection;
  split?: SplitType;
  scope?: ScopeType;
  frequency?: string; // For animations like bounce, wiggle (e.g., "2 Hz")
  scale?: string; // Scale factor (e.g., "50%")
  x_angle?: string; // Rotation angle (e.g., "20")
  y_angle?: string;
  z_angle?: string;
  rotation?: string; // For spin animation (e.g., "360")
  background_effect?: string; // Background effect for certain animations

  // Anchor points for scaling/rotation
  x_anchor?: string; // Anchor point for x-axis (e.g., "50%", "100%")
  y_anchor?: string; // Anchor point for y-axis (e.g., "50%", "100%")

  // Custom cubic bezier curves
  cubic_bezier?: [number, number, number, number];

  // Steps for step easing
  steps?: number;
}

export interface Overlay {
  time: number | 'start' | 'end'; // Time in seconds or 'start'/'end'
  easing: string;
  value: string;
  id?: string;
}

export type EasingType =
  | 'linear'
  | 'cubic-bezier'
  | 'steps'
  | 'elastic-in'
  | 'elastic-out'
  | 'elastic-in-out'
  | 'bounce-in'
  | 'bounce-out'
  | 'bounce-in-out'
  | 'sinusoid-in'
  | 'sinusoid-out'
  | 'sinusoid-in-out'
  | 'quadratic-in'
  | 'quadratic-out'
  | 'quadratic-in-out'
  | 'cubic-in'
  | 'cubic-out'
  | 'cubic-in-out'
  | 'quartic-in'
  | 'quartic-out'
  | 'quartic-in-out'
  | 'quintic-in'
  | 'quintic-out'
  | 'quintic-in-out'
  | 'exponential-in'
  | 'exponential-out'
  | 'exponential-in-out'
  | 'circular-in'
  | 'circular-out'
  | 'circular-in-out'
  | 'back-in'
  | 'back-out'
  | 'back-in-out';

export type AnimationDirection =
  | 'up'
  | 'down'
  | 'left'
  | 'right'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

export type SplitType = 'none' | 'letter' | 'word' | 'line';

export type ScopeType = 'split-clip' | 'element' | 'composition';

// Common animation types available across all elements
export const CommonAnimationTypes = {
  fade: 'Fade',
  scale: 'Scale',
  slide: 'Slide',
  pan: 'Pan',
  'film-roll': 'Film Roll',
  flip: 'Flip',
  shift: 'Shift',
} as const;

// Text-specific animation types
export const TextAnimationTypes = {
  'text-appear': 'Appear',
  'text-slide': 'Slide',
  'text-reveal': 'Reveal',
  'text-fly': 'Fly',
  'text-typewriter': 'Typewriter',
  ...CommonAnimationTypes,
} as const;

// Image/Video specific animation types
export const MediaAnimationTypes = {
  wipe: 'Wipe',
  'color-wipe': 'Color Wipe',
  'circular-wipe': 'Circular Wipe',
  squash: 'Squash',
  spin: 'Spin',
  stripe: 'Stripe',
  shake: 'Shake',
  bounce: 'Bounce',
  wiggle: 'Wiggle',
  ...CommonAnimationTypes,
} as const;

export type CommonAnimationType = keyof typeof CommonAnimationTypes;
export type TextAnimationType = keyof typeof TextAnimationTypes;
export type MediaAnimationType = keyof typeof MediaAnimationTypes;

/**
 * Keyframe animation for property-based animations
 * Allows animating any element property over time
 */
export interface Keyframe {
  time: string | number; // Time in seconds or 'start'/'end'
  value: any; // The value at this keyframe
  easing?: EasingType;
}

/**
 * Interface for elements that support animations
 */
export interface AnimationSupport {
  animations?: Animation[];

  // Keyframe-based animations for properties
  [property: string]: Keyframe[] | any;
}

/**
 * Animation utility functions
 */
export class AnimationUtils {
  /**
   * Creates a basic fade animation
   */
  static createFadeAnimation(
    time: string | number = 'start',
    duration: number = 1,
    reversed: boolean = false,
  ): Animation {
    return {
      type: 'fade',
      time,
      duration,
      easing: 'quadratic-out',
      ...(reversed && { reversed: true }),
    };
  }

  /**
   * Creates a text slide animation
   */
  static createTextSlideAnimation(
    direction: AnimationDirection = 'up',
    time: string | number = 'start',
    duration: number = 1,
    split: SplitType = 'letter',
    reversed: boolean = false,
  ): Animation {
    return {
      type: 'text-slide',
      direction,
      time,
      duration,
      split,
      scope: 'split-clip',
      easing: 'quadratic-out',
      ...(reversed && { reversed: true }),
    };
  }

  /**
   * Creates a typewriter animation
   */
  static createTypewriterAnimation(
    time: string | number = 'start',
    duration?: number,
  ): Animation {
    const animation: Animation = {
      type: 'text-typewriter',
      easing: 'linear',
    };

    if (time !== undefined) {
      animation.time = time;
      if (duration !== undefined) {
        animation.duration = duration;
      }
    }

    return animation;
  }

  /**
   * Creates a scale animation
   */
  static createScaleAnimation(
    time: string | number = 'start',
    duration: number = 1,
    scale: string = '150%',
    reversed: boolean = false,
  ): Animation {
    return {
      type: 'scale',
      time,
      duration,
      scale,
      easing: 'quadratic-out',
      ...(reversed && { reversed: true }),
    };
  }

  /**
   * Creates a bounce animation
   */
  static createBounceAnimation(
    time: string | number,
    duration: number = 1,
    frequency: string = '2 Hz',
    scale: string = '50%',
    y_anchor: string = '100%',
  ): Animation {
    return {
      type: 'bounce',
      time,
      duration,
      frequency,
      scale,
      y_anchor,
      easing: 'linear',
    };
  }

  /**
   * Validates an animation object
   */
  static validateAnimation(animation: Animation): boolean {
    if (!animation.type) {
      return false;
    }

    // Validate time if specified
    if (animation.time !== undefined) {
      if (
        typeof animation.time === 'string' &&
        !['start', 'end'].includes(animation.time)
      ) {
        return false;
      }
      if (typeof animation.time === 'number' && animation.time < 0) {
        return false;
      }
    }

    // Validate duration if specified
    if (animation.duration !== undefined && animation.duration <= 0) {
      return false;
    }

    return true;
  }

  /**
   * Removes an animation by type and time
   */
  static removeAnimation(
    animations: Animation[],
    type?: string,
    time?: string | number,
  ): Animation[] {
    return animations.filter(animation => {
      if (type && animation.type !== type) {
        return true;
      }
      if (time !== undefined && animation.time !== time) {
        return true;
      }
      return false;
    });
  }

  /**
   * Updates or adds an animation
   */
  static setAnimation(
    animations: Animation[],
    newAnimation: Animation,
  ): Animation[] {
    // Remove existing animation with same type and time
    const filtered = AnimationUtils.removeAnimation(
      animations,
      newAnimation.type,
      newAnimation.time,
    );

    // Add the new animation if it's valid
    if (AnimationUtils.validateAnimation(newAnimation)) {
      filtered.push(newAnimation);
    }

    return filtered;
  }

  /**
   * Gets enter animation (time: start or 0)
   */
  static getEnterAnimation(animations: Animation[]): Animation | undefined {
    return animations.find(
      animation => animation.time === 'start' || animation.time === 0,
    );
  }

  /**
   * Gets exit animation (time: end)
   */
  static getExitAnimation(animations: Animation[]): Animation | undefined {
    return animations.find(animation => animation.time === 'end');
  }
}
